import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'ES';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'ES' ? 'EN' : 'ES');
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations = {
  ES: {
    nav: {
      home: 'Home',
      printing3d: 'Impresión 3D',
      modular: 'Modular',
      about: 'Nosotros',
      contact: 'Contacto',
      scheduleCall: 'Agendar Llamada',
      whatsapp: 'WhatsApp'
    },
    hero: {
      badge: 'Construcción Industrializada',
      title1: 'CONSTRUCCIÓN',
      title2: 'MODULAR',
      subtitle: 'DISEÑO · ELABORACIÓN · ENTREGA',
      modular: 'Modular',
      concrete: 'Muro Hormigón',
      from: 'm² desde',
      cta1: 'Solicitar Propuesta',
      cta2: 'Conocer Más',
      scroll: 'Scroll'
    },
    stats: {
      badge: 'Resultados Comprobados',
      title: 'Más Rápido que la Construcción Tradicional',
      production: 'Producción Anual',
      costReduction: 'Reducción de Costos',
      profitability: 'Más Rentabilidad',
      faster: 'Más Rápido',
      disclaimer: '* Valores de referencia sujetos a ingeniería, logística y aprobaciones locales'
    },
    services: {
      badge: 'Nuestras Soluciones',
      title: 'Dos Tecnologías, Un Objetivo',
      subtitle: 'Velocidad, control de costos, estandarización y escalabilidad para tu proyecto',
      concrete: {
        badge: 'Tecnología COSMOS',
        title: 'Impresión 3D de Hormigón',
        description: 'Fabricación de muros, perímetros, paredes y estructuras complejas con tecnología alemana bajo estándares europeos.',
        price: 'desde u$s 195/m²',
        feature1: 'Materiales locales adaptables',
        feature2: 'Enfoque industrial escalable',
        feature3: 'Alta productividad garantizada'
      },
      modular: {
        badge: 'Plataforma MODE',
        title: 'Unidades Modulares',
        description: 'Casas modulares con diseño británico y manufactura china: eficiencia, calidad y entrega rápida.',
        price: 'desde u$s 395/m²',
        feature1: 'Sistema integrado de delivery',
        feature2: 'Control de calidad europeo',
        feature3: 'Replicable globalmente'
      },
      viewMore: 'Ver Más'
    },
    products: {
      badge: 'Valores por Grandes Volúmenes',
      title: 'Valores de Referencia',
      subtitle: 'Soluciones para cada tipo de proyecto, desde vivienda social hasta desarrollos premium',
      from: 'desde',
      product1: {
        title: 'Casas de Barrio Cerrado',
        description: 'Muros de hormigón con terminaciones premium y módulos listos en tiempo récord.'
      },
      product2: {
        title: 'Viviendas Sociales',
        description: 'Soluciones habitacionales asequibles, duraderas y con estándares europeos.'
      },
      product3: {
        title: 'Desarrollo Premium',
        description: 'Arquitectura modular de lujo con diseño europeo y eficiencia energética.'
      },
      product4: {
        title: 'Alojamiento Minero',
        description: 'Campamentos modulares resistentes, transportables y confortables.'
      },
      product5: {
        title: 'Hospitales y Escuelas',
        description: 'Infraestructura educativa y sanitaria modular de calidad europea.'
      },
      product6: {
        title: 'Hoteles de 1 a 4 estrellas',
        description: 'Hoteles modulares de estándar internacional con diseño flexible.'
      },
      disclaimer: '* Valores de referencia. Los precios finales dependen del volumen, ubicación y especificaciones del proyecto.'
    },
    business: {
      badge: 'Modelos Comerciales',
      title: 'Capex vs Opex:',
      subtitle: 'Elegí tu modelo',
      description: 'Adaptamos nuestra propuesta a tu estructura financiera y objetivos.',
      model1: {
        title: 'Venta de Equipo',
        description: 'Adquisición directa de maquinaria de impresión 3D y sistemas modulares.'
      },
      model2: {
        title: 'Servicio por m²',
        description: 'Printing-as-a-service: pagás solo por lo que construís.'
      },
      model3: {
        title: 'JV / SPV',
        description: 'Joint Venture o Special Purpose Vehicle por proyecto específico.'
      },
      model4: {
        title: 'EPC Llave en Mano',
        description: 'Proyecto completo con partners: diseño, construcción y entrega.'
      },
      investment: 'Inversión',
      flexible: 'Flexible',
      returns: 'Retorno',
      optimised: 'Optimizado'
    },
    standards: {
      badge: 'Calidad',
      title: 'Velocidad, Calidad y Tecnología',
      standard1: {
        title: 'Altos Estándares',
        description: 'Nuestras construcciones modulares cumplen con los más altos estándares europeos, aplicados en hoteles de Londres y desarrollos habitacionales en mercados exigentes.',
        feature1: 'Eficiencia garantizada',
        feature2: 'Durabilidad comprobada',
        feature3: 'Diseño contemporáneo'
      },
      standard2: {
        title: 'Diseños Homologados',
        description: 'Ofrecemos diseños homologados que aseguran un proceso constructivo ágil y seguro, con detalles en las terminaciones que marcan la diferencia.',
        feature1: 'Acabados premium',
        feature2: 'Aislamiento térmico',
        feature3: 'Estética moderna'
      }
    },
    trust: {
      badge: 'Confianza',
      title: '¿Por qué elegirnos?',
      point1: {
        title: 'We Develop + We Advise',
        description: 'Operamos como integrador y partner comercial, con capacidad de originar deals.'
      },
      point2: {
        title: 'Red CIBA',
        description: 'Acceso a nuestra red del Congreso CIBA como plataforma de originación.'
      },
      point3: {
        title: 'Estándares Europeos',
        description: 'Tecnología alemana y diseño británico bajo los más altos estándares.'
      },
      point4: {
        title: 'Equipo Especializado',
        description: 'Profesionales con experiencia en construcción industrializada.'
      },
      disclaimer: 'All figures are indicative, subject to engineering, logistics, duties and local approvals.'
    },
    faq: {
      badge: 'FAQ',
      title: 'Preguntas Frecuentes',
      q1: '¿Cuál es el tiempo de entrega típico?',
      a1: 'Dependiendo del proyecto, entre 30-90 días para módulos y 2-4 semanas para muros de hormigón impresos.',
      q2: '¿Los precios incluyen transporte?',
      a2: 'Los valores son de referencia FOB. El transporte e instalación se cotizan según ubicación del proyecto.',
      q3: '¿Qué garantías ofrecen?',
      a3: 'Garantía estructural de 10 años y 2 años en terminaciones, siguiendo estándares europeos.',
      q4: '¿Pueden trabajar con materiales locales?',
      a4: 'Sí, nuestros sistemas están diseñados para adaptarse a materiales disponibles localmente.'
    },
    cta: {
      badge: 'Respuesta en 24 horas',
      title: 'Cotiza tu proyecto en 24hs',
      subtitle: 'Revolucionamos la construcción: rápido, accesible y de calidad internacional.',
      scheduleCall: 'Agendar Llamada',
      whatsapp: 'WhatsApp',
      supportedBy: 'Con el apoyo de:'
    },
    footer: {
      description: 'Construcción industrializada para Argentina y Uruguay. Impresión 3D de hormigón + modular premium.',
      navigation: 'Navegación',
      services: 'Servicios',
      contact: 'Contacto',
      service1: 'Impresión 3D de Hormigón',
      service2: 'Construcción Modular',
      service3: 'Viviendas Prefabricadas',
      service4: 'Proyectos Industriales',
      service5: 'Consultoría Técnica',
      supportedBy: 'Con el apoyo de:',
      rights: 'Todos los derechos reservados.',
      disclaimer: 'All figures are indicative, subject to engineering, logistics, duties and local approvals.'
    },
    contact: {
      badge: 'Contacto',
      title: 'Cotiza tu proyecto',
      title2: 'en 24 horas',
      subtitle: 'Revolucionamos la construcción: rápido, accesible y de calidad internacional.',
      infoTitle: 'Información de Contacto',
      email: 'Email',
      whatsapp: 'WhatsApp',
      directChat: 'Chat directo',
      location: 'Ubicación',
      hours: 'Horario',
      hoursValue: 'Lun - Vie: 9:00 - 18:00',
      scheduleCall: 'Agendar Llamada',
      writeWhatsapp: 'Escribir por WhatsApp',
      formTitle: 'Solicitar Propuesta',
      formSubtitle: 'Completá el formulario y te respondemos en menos de 24 horas.',
      name: 'Nombre',
      company: 'Empresa',
      country: 'País',
      selectCountry: 'Seleccionar país',
      projectType: 'Tipo de Construcción',
      selectType: 'Seleccionar tipo',
      sqMeters: 'm² o Unidades',
      timeline: 'Plazo Estimado',
      message: 'Mensaje',
      messagePlaceholder: 'Contános más sobre tu proyecto...',
      consent: 'Acepto recibir información de We Develop.',
      submit: 'Enviar Solicitud',
      sending: 'Enviando...',
      thankYou: '¡Gracias por contactarnos!',
      response: 'Nos comunicaremos contigo en las próximas 24 horas.',
      types: {
        single: 'Vivienda unifamiliar',
        multi: 'Vivienda multifamiliar',
        industrial: 'Industrial / Nave',
        oilgas: 'Oil & Gas / Minería',
        health: 'Hospitales / Escuelas',
        hotels: 'Hoteles',
        offices: 'Oficinas',
        other: 'Otro'
      }
    },
    about: {
      badge: 'Sobre Nosotros',
      title: 'Transformando la',
      title2: 'Construcción en LATAM',
      subtitle: 'We Develop opera como integrador y partner comercial, con capacidad de originar deals a través de We Advise y el Congreso CIBA.',
      mission: {
        title: 'Nuestra Misión',
        description: 'Traer a Latinoamérica las mejores tecnologías de construcción industrializada: impresión 3D de hormigón y construcción modular premium.'
      },
      reach: {
        title: 'Alcance Regional',
        description: 'Operamos principalmente en Argentina y Uruguay, con planes de expansión regional.'
      },
      quality: {
        title: 'Estándares de Calidad',
        description: 'Trabajamos bajo estándares europeos, con tecnología probada en los mercados más exigentes.'
      },
      networkBadge: 'Nuestra Red',
      networkTitle: 'Ecosistema We Develop',
      network1: {
        title: 'We Develop',
        description: 'Integrador y partner comercial para proyectos de construcción industrializada.'
      },
      network2: {
        title: 'We Advise',
        description: 'Consultoría especializada y originación de deals para inversores.'
      },
      network3: {
        title: 'Congreso CIBA',
        description: 'Plataforma de networking y originación de negocios.'
      },
      supportedBy: 'Con el apoyo de:'
    },
    concrete: {
      badge: 'Tecnología COSMOS',
      title: 'Impresión 3D',
      title2: 'de Hormigón',
      subtitle: 'Fabricación de muros, perímetros y estructuras complejas con tecnología alemana bajo estándares europeos.',
      cta1: 'Solicitar Propuesta',
      cta2: 'Dossier Técnico',
      featuresBadge: 'Características',
      featuresTitle: 'Tecnología de Vanguardia',
      feature1: {
        title: 'Materiales Locales Adaptables',
        description: 'Sistema diseñado para trabajar con materiales disponibles localmente.'
      },
      feature2: {
        title: 'Enfoque Industrial Escalable',
        description: 'Pensado para producción en escala, ideal para desarrollos masivos.'
      },
      feature3: {
        title: 'Alta Productividad',
        description: 'Velocidad de impresión optimizada para cumplir plazos exigentes.'
      },
      feature4: {
        title: 'Tecnología Alemana',
        description: 'Maquinaria con estándares europeos de precisión y confiabilidad.'
      },
      resultBadge: 'Resultado Final',
      resultTitle: 'Casa Construida con Impresión 3D',
      resultDescription: 'Mirá el resultado de nuestra tecnología de impresión 3D de hormigón. Muros estructurales de alta resistencia, terminaciones de primera calidad, y tiempos de construcción reducidos en un 70% comparado con métodos tradicionales.',
      faster: 'Más rápido',
      costReduction: 'Reducción de costos',
      clickToWatch: 'Click para ver el video',
      applicationsBadge: 'Aplicaciones',
      applicationsTitle: '¿Qué podemos construir?',
      app1: 'Muros perimetrales',
      app2: 'Viviendas unifamiliares',
      app3: 'Estructuras industriales',
      app4: 'Elementos arquitectónicos',
      consult: 'consultar'
    },
    modular: {
      badge: 'Plataforma MODE',
      title: 'Construcción',
      title2: 'Modular',
      subtitle: 'Casas modulares con diseño británico y manufactura china: eficiencia, calidad y entrega garantizada.',
      cta1: 'Solicitar Propuesta',
      cta2: 'Catálogo Modular',
      featuresBadge: 'Ventajas',
      featuresTitle: 'Diseño Británico, Manufactura China',
      feature1: {
        title: 'Sistema Integrado',
        description: 'Plataforma completa: diseño, fabricación y delivery en un solo proceso.'
      },
      feature2: {
        title: 'Entrega Garantizada',
        description: 'Logística coordinada desde fábrica hasta sitio de montaje.'
      },
      feature3: {
        title: 'Control de Calidad',
        description: 'Inspección en fábrica bajo estándares europeos.'
      },
      feature4: {
        title: 'Replicable Globalmente',
        description: 'Diseños estandarizados adaptables a cualquier ubicación.'
      },
      solutionsBadge: 'Soluciones',
      solutionsTitle: 'Tipos de Proyecto',
      project1: 'Vivienda Social',
      project2: 'Barrios Cerrados',
      project3: 'Desarrollo Premium',
      project4: 'Hoteles',
      processBadge: 'Proceso',
      processTitle: 'De Diseño a Entrega',
      step1: { title: 'Diseño', description: 'Personalización del proyecto' },
      step2: { title: 'Fabricación', description: 'Producción en planta' },
      step3: { title: 'Transporte', description: 'Logística internacional' },
      step4: { title: 'Montaje', description: 'Instalación en sitio' }
    }
  },
  EN: {
    nav: {
      home: 'Home',
      printing3d: '3D Printing',
      modular: 'Modular',
      about: 'About Us',
      contact: 'Contact',
      scheduleCall: 'Schedule a Call',
      whatsapp: 'WhatsApp'
    },
    hero: {
      badge: 'Industrialised Construction',
      title1: 'MODULAR',
      title2: 'CONSTRUCTION',
      subtitle: 'DESIGN · MANUFACTURE · DELIVERY',
      modular: 'Modular',
      concrete: 'Concrete Wall',
      from: 'sqm from',
      cta1: 'Request a Quote',
      cta2: 'Learn More',
      scroll: 'Scroll'
    },
    stats: {
      badge: 'Proven Results',
      title: 'Faster Than Traditional Construction',
      production: 'Annual Production',
      costReduction: 'Cost Reduction',
      profitability: 'More Profitable',
      faster: 'Faster',
      disclaimer: '* Reference values subject to engineering, logistics and local approvals'
    },
    services: {
      badge: 'Our Solutions',
      title: 'Two Technologies, One Goal',
      subtitle: 'Speed, cost control, standardisation and scalability for your project',
      concrete: {
        badge: 'COSMOS Technology',
        title: '3D Concrete Printing',
        description: 'Manufacturing of walls, perimeters and complex structures with German technology under European standards.',
        price: 'from USD 195/sqm',
        feature1: 'Adaptable local materials',
        feature2: 'Scalable industrial approach',
        feature3: 'High productivity guaranteed'
      },
      modular: {
        badge: 'MODE Platform',
        title: 'Modular Units',
        description: 'Modular houses with British design and Chinese manufacturing: efficiency, quality and fast delivery.',
        price: 'from USD 395/sqm',
        feature1: 'Integrated delivery system',
        feature2: 'European quality control',
        feature3: 'Globally replicable'
      },
      viewMore: 'View More'
    },
    products: {
      badge: 'Volume Pricing',
      title: 'Reference Prices',
      subtitle: 'Solutions for every project type, from social housing to premium developments',
      from: 'from',
      product1: {
        title: 'Gated Community Homes',
        description: 'Concrete walls with premium finishes and modules ready in record time.'
      },
      product2: {
        title: 'Social Housing',
        description: 'Affordable, durable housing solutions built to European standards.'
      },
      product3: {
        title: 'Premium Development',
        description: 'Luxury modular architecture with European design and energy efficiency.'
      },
      product4: {
        title: 'Mining Accommodation',
        description: 'Durable, transportable and comfortable modular camps.'
      },
      product5: {
        title: 'Hospitals & Schools',
        description: 'European-quality modular educational and healthcare infrastructure.'
      },
      product6: {
        title: '1 to 4 Star Hotels',
        description: 'International standard modular hotels with flexible design.'
      },
      disclaimer: '* Reference values. Final prices depend on volume, location and project specifications.'
    },
    business: {
      badge: 'Business Models',
      title: 'Capex vs Opex:',
      subtitle: 'Choose your model',
      description: 'We adapt our proposal to your financial structure and objectives.',
      model1: {
        title: 'Equipment Sale',
        description: 'Direct acquisition of 3D printing machinery and modular systems.'
      },
      model2: {
        title: 'Service per sqm',
        description: 'Printing-as-a-service: you only pay for what you build.'
      },
      model3: {
        title: 'JV / SPV',
        description: 'Joint Venture or Special Purpose Vehicle for specific projects.'
      },
      model4: {
        title: 'Turnkey EPC',
        description: 'Complete project with partners: design, construction and delivery.'
      },
      investment: 'Investment',
      flexible: 'Flexible',
      returns: 'Returns',
      optimised: 'Optimised'
    },
    standards: {
      badge: 'Quality',
      title: 'Speed, Quality and Technology',
      standard1: {
        title: 'High Standards',
        description: 'Our modular constructions meet the highest European standards, applied in London hotels and housing developments in demanding markets.',
        feature1: 'Guaranteed efficiency',
        feature2: 'Proven durability',
        feature3: 'Contemporary design'
      },
      standard2: {
        title: 'Approved Designs',
        description: 'We offer approved designs that ensure an agile and safe construction process, with finishing details that make the difference.',
        feature1: 'Premium finishes',
        feature2: 'Thermal insulation',
        feature3: 'Modern aesthetics'
      }
    },
    trust: {
      badge: 'Trust',
      title: 'Why Choose Us?',
      point1: {
        title: 'We Develop + We Advise',
        description: 'We operate as integrator and commercial partner, with deal origination capability.'
      },
      point2: {
        title: 'CIBA Network',
        description: 'Access to our CIBA Congress network as an origination platform.'
      },
      point3: {
        title: 'European Standards',
        description: 'German technology and British design under the highest standards.'
      },
      point4: {
        title: 'Specialised Team',
        description: 'Professionals with experience in industrialised construction.'
      },
      disclaimer: 'All figures are indicative, subject to engineering, logistics, duties and local approvals.'
    },
    faq: {
      badge: 'FAQ',
      title: 'Frequently Asked Questions',
      q1: 'What is the typical delivery time?',
      a1: 'Depending on the project, between 30-90 days for modules and 2-4 weeks for 3D printed concrete walls.',
      q2: 'Do prices include transport?',
      a2: 'Values are FOB reference. Transport and installation are quoted according to project location.',
      q3: 'What guarantees do you offer?',
      a3: '10-year structural guarantee and 2 years on finishes, following European standards.',
      q4: 'Can you work with local materials?',
      a4: 'Yes, our systems are designed to adapt to locally available materials.'
    },
    cta: {
      badge: 'Response within 24 hours',
      title: 'Get a quote within 24hrs',
      subtitle: 'We revolutionise construction: fast, affordable and international quality.',
      scheduleCall: 'Schedule a Call',
      whatsapp: 'WhatsApp',
      supportedBy: 'Supported by:'
    },
    footer: {
      description: 'Industrialised construction for Argentina and Uruguay. 3D concrete printing + premium modular.',
      navigation: 'Navigation',
      services: 'Services',
      contact: 'Contact',
      service1: '3D Concrete Printing',
      service2: 'Modular Construction',
      service3: 'Prefabricated Housing',
      service4: 'Industrial Projects',
      service5: 'Technical Consultancy',
      supportedBy: 'Supported by:',
      rights: 'All rights reserved.',
      disclaimer: 'All figures are indicative, subject to engineering, logistics, duties and local approvals.'
    },
    contact: {
      badge: 'Contact',
      title: 'Quote your project',
      title2: 'within 24 hours',
      subtitle: 'We revolutionise construction: fast, affordable and international quality.',
      infoTitle: 'Contact Information',
      email: 'Email',
      whatsapp: 'WhatsApp',
      directChat: 'Direct chat',
      location: 'Location',
      hours: 'Hours',
      hoursValue: 'Mon - Fri: 9:00 - 18:00',
      scheduleCall: 'Schedule a Call',
      writeWhatsapp: 'Message on WhatsApp',
      formTitle: 'Request a Quote',
      formSubtitle: 'Complete the form and we will respond within 24 hours.',
      name: 'Name',
      company: 'Company',
      country: 'Country',
      selectCountry: 'Select country',
      projectType: 'Construction Type',
      selectType: 'Select type',
      sqMeters: 'sqm or Units',
      timeline: 'Estimated Timeline',
      message: 'Message',
      messagePlaceholder: 'Tell us more about your project...',
      consent: 'I agree to receive information from We Develop.',
      submit: 'Submit Request',
      sending: 'Sending...',
      thankYou: 'Thank you for contacting us!',
      response: 'We will be in touch within 24 hours.',
      types: {
        single: 'Single-family housing',
        multi: 'Multi-family housing',
        industrial: 'Industrial / Warehouse',
        oilgas: 'Oil & Gas / Mining',
        health: 'Hospitals / Schools',
        hotels: 'Hotels',
        offices: 'Offices',
        other: 'Other'
      }
    },
    about: {
      badge: 'About Us',
      title: 'Transforming',
      title2: 'Construction in LATAM',
      subtitle: 'We Develop operates as integrator and commercial partner, with deal origination capability through We Advise and the CIBA Congress.',
      mission: {
        title: 'Our Mission',
        description: 'Bringing the best industrialised construction technologies to Latin America: 3D concrete printing and premium modular construction.'
      },
      reach: {
        title: 'Regional Reach',
        description: 'We operate mainly in Argentina and Uruguay, with regional expansion plans.'
      },
      quality: {
        title: 'Quality Standards',
        description: 'We work under European standards, with technology proven in the most demanding markets.'
      },
      networkBadge: 'Our Network',
      networkTitle: 'We Develop Ecosystem',
      network1: {
        title: 'We Develop',
        description: 'Integrator and commercial partner for industrialised construction projects.'
      },
      network2: {
        title: 'We Advise',
        description: 'Specialised consultancy and deal origination for investors.'
      },
      network3: {
        title: 'CIBA Congress',
        description: 'Networking and business origination platform.'
      },
      supportedBy: 'Supported by:'
    },
    concrete: {
      badge: 'COSMOS Technology',
      title: '3D Concrete',
      title2: 'Printing',
      subtitle: 'Manufacturing of walls, perimeters and complex structures with German technology under European standards.',
      cta1: 'Request a Quote',
      cta2: 'Technical Dossier',
      featuresBadge: 'Features',
      featuresTitle: 'Cutting-Edge Technology',
      feature1: {
        title: 'Adaptable Local Materials',
        description: 'System designed to work with locally available materials.'
      },
      feature2: {
        title: 'Scalable Industrial Approach',
        description: 'Designed for scale production, ideal for mass developments.'
      },
      feature3: {
        title: 'High Productivity',
        description: 'Printing speed optimised to meet demanding deadlines.'
      },
      feature4: {
        title: 'German Technology',
        description: 'Machinery with European standards of precision and reliability.'
      },
      resultBadge: 'Final Result',
      resultTitle: 'House Built with 3D Printing',
      resultDescription: 'See the result of our 3D concrete printing technology. High-strength structural walls, top-quality finishes, and construction times reduced by 70% compared to traditional methods.',
      faster: 'Faster',
      costReduction: 'Cost reduction',
      clickToWatch: 'Click to watch video',
      applicationsBadge: 'Applications',
      applicationsTitle: 'What can we build?',
      app1: 'Perimeter walls',
      app2: 'Single-family homes',
      app3: 'Industrial structures',
      app4: 'Architectural elements',
      consult: 'enquire'
    },
    modular: {
      badge: 'MODE Platform',
      title: 'Modular',
      title2: 'Construction',
      subtitle: 'Modular houses with British design and Chinese manufacturing: efficiency, quality and guaranteed delivery.',
      cta1: 'Request a Quote',
      cta2: 'Modular Catalogue',
      featuresBadge: 'Advantages',
      featuresTitle: 'British Design, Chinese Manufacturing',
      feature1: {
        title: 'Integrated System',
        description: 'Complete platform: design, manufacturing and delivery in one process.'
      },
      feature2: {
        title: 'Guaranteed Delivery',
        description: 'Coordinated logistics from factory to assembly site.'
      },
      feature3: {
        title: 'Quality Control',
        description: 'Factory inspection under European standards.'
      },
      feature4: {
        title: 'Globally Replicable',
        description: 'Standardised designs adaptable to any location.'
      },
      solutionsBadge: 'Solutions',
      solutionsTitle: 'Project Types',
      project1: 'Social Housing',
      project2: 'Gated Communities',
      project3: 'Premium Development',
      project4: 'Hotels',
      processBadge: 'Process',
      processTitle: 'From Design to Delivery',
      step1: { title: 'Design', description: 'Project customisation' },
      step2: { title: 'Manufacturing', description: 'Factory production' },
      step3: { title: 'Transport', description: 'International logistics' },
      step4: { title: 'Assembly', description: 'On-site installation' }
    }
  }
};

export default LanguageContext;
